import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { NumberInput } from '../../components/ui/number-input';

const UNIT_OPTIONS = ['cm', 'in', 'mm', 'm'];

export interface SpecificationsData {
	dimensions: {
		length: number;
		width: number;
		height: number;
		unit: string;
	};
	weight: number;
}

export function AddSpecificationsModal({
	open,
	onOpenChange,
	onSubmit,
	loading,
	initialValues,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (data: SpecificationsData) => void;
	loading?: boolean;
	initialValues?: {
		length?: number | null;
		width?: number | null;
		height?: number | null;
		weight?: number | null;
		unit?: string | null;
	};
}) {
	const toStr = (v?: number | null) => (v && v > 0 ? String(v) : '');
	const [length, setLength] = useState(toStr(initialValues?.length));
	const [width, setWidth] = useState(toStr(initialValues?.width));
	const [height, setHeight] = useState(toStr(initialValues?.height));
	const [unit, setUnit] = useState(initialValues?.unit || 'cm');
	const [weight, setWeight] = useState(toStr(initialValues?.weight));

	const hasAnyValue = length || width || height || weight;

	const handleSubmit = () => {
		if (!hasAnyValue) return;
		onSubmit({
			dimensions: {
				length: parseFloat(length) || 0,
				width: parseFloat(width) || 0,
				height: parseFloat(height) || 0,
				unit,
			},
			weight: parseFloat(weight) || 0,
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Add specifications</DialogTitle>
					<DialogDescription>
						Add dimensions and weight to help with sizing and shipping.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{/* Dimensions */}
							<div>
								<Label className="mb-2 block text-sm font-medium text-text-1">
									Dimensions
								</Label>
								<div className="grid grid-cols-3 gap-2">
									<div className="flex flex-col gap-1">
										<Label className="text-xs text-text-3">Length</Label>
										<NumberInput
											value={length}
											onChange={(e) =>
												setLength((e.target as HTMLInputElement).value)
											}
											placeholder="0"
											min={0}
											step={0.01}
										/>
									</div>
									<div className="flex flex-col gap-1">
										<Label className="text-xs text-text-3">Width</Label>
										<NumberInput
											value={width}
											onChange={(e) =>
												setWidth((e.target as HTMLInputElement).value)
											}
											placeholder="0"
											min={0}
											step={0.01}
										/>
									</div>
									<div className="flex flex-col gap-1">
										<Label className="text-xs text-text-3">Height</Label>
										<NumberInput
											value={height}
											onChange={(e) =>
												setHeight((e.target as HTMLInputElement).value)
											}
											placeholder="0"
											min={0}
											step={0.01}
										/>
									</div>
								</div>
							</div>

							{/* Unit */}
							<div className="flex flex-col gap-1.5">
								<Label>Unit</Label>
								<div className="flex gap-1.5">
									{UNIT_OPTIONS.map((u) => (
										<button
											type="button"
											key={u}
											onClick={() => setUnit(u)}
											className={`flex h-8 cursor-pointer items-center justify-center rounded-lg border px-4 text-xs font-medium transition-colors ${
												unit === u
													? 'border-accent bg-accent-soft text-accent'
													: 'border-line-2 bg-transparent text-text-2 hover:border-line-3'
											}`}
										>
											{u}
										</button>
									))}
								</div>
							</div>

							{/* Weight */}
							<div className="flex flex-col gap-1.5">
								<Label>Weight (kg)</Label>
								<NumberInput
									value={weight}
									onChange={(e) =>
										setWeight((e.target as HTMLInputElement).value)
									}
									placeholder="0"
									min={0}
									step={0.01}
									className="max-w-[200px]"
								/>
								<p className="text-xs text-text-4">Product weight in kilograms</p>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!hasAnyValue || loading}>
							{loading ? 'Saving…' : 'Save specifications'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
